import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx, clsx } from '@payaca/utilities/cvaMerge';

import React, {
  FC,
  Fragment,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from '../Button/Button';

export interface IPanelProps {
  className?: string;
  label: string;
  render: (selected: boolean) => ReactElement<any>;
}

const Panel: FC<IPanelProps> = (props) => <></>;

type Variant = 'default' | 'segment' | 'pilled';

export interface TabsProps {
  className?: string;
  classNameTabPanels?: string;
  selectedIndex?: number;
  defaultIndex?: number;
  onChange?: (index: number) => void;
  fullWidth?: boolean;
  variant?: Variant;
  suffix?: ReactNode;
  /**
   * When true, the tab list scrolls horizontally with chevron buttons that
   * appear when content overflows. Incompatible with `fullWidth`.
   */
  scrollable?: boolean;
}

const Tabs: FC<PropsWithChildren<TabsProps>> = (props) => {
  const {
    className,
    classNameTabPanels,
    fullWidth = false,
    selectedIndex,
    defaultIndex,
    onChange,
    variant = 'default',
    suffix,
    scrollable = false,

    children,
  } = props;

  if (scrollable && fullWidth) {
    throw new Error(
      'Tabs: `scrollable` and `fullWidth` are mutually exclusive — ' +
        'a scrollable tab list cannot stretch to fill its container.'
    );
  }

  const tabPanels = React.Children.toArray(children).reduce<
    Array<{ key: string; props: IPanelProps }>
  >((acc, child, index) => {
    if (!isValidElement(child)) {
      return acc;
    }

    // In React 19, props are now 'unknown' instead of 'any'
    const props = child.props as any;
    if (
      typeof props.label === 'undefined' ||
      typeof props.render === 'undefined'
    ) {
      return acc;
    }

    acc.push({ key: child.key ?? `${props.label}-${index}`, props });

    return acc;
  }, []);

  const {
    ref: scrollRef,
    canScrollLeft,
    canScrollRight,
    scrollByAmount,
  } = useScrollOverflow();

  // Scroll the selected tab into view when the controlled index changes.
  useEffect(() => {
    if (!scrollable || selectedIndex == null) return;
    const tab =
      scrollRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[
        selectedIndex
      ];
    tab?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [scrollable, selectedIndex, scrollRef]);

  const tabList = scrollable ? (
    <div
      className={clstx(
        'flex min-w-0 flex-1 items-center gap-x-1',
        !suffix && className
      )}
    >
      <ScrollChevron
        direction="left"
        visible={canScrollLeft}
        onClick={() => scrollByAmount(-200)}
      />
      <div
        ref={scrollRef}
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values
        className="min-w-0 flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      >
        <PlTabList variant={variant} className="w-max flex-nowrap">
          {tabPanels.map(({ key, props: column }) => {
            return (
              <PlTab key={key} fullWidth={false} variant={variant}>
                {column.label}
              </PlTab>
            );
          })}
        </PlTabList>
      </div>
      <ScrollChevron
        direction="right"
        visible={canScrollRight}
        onClick={() => scrollByAmount(200)}
      />
    </div>
  ) : (
    <PlTabList variant={variant} className={!suffix ? className : undefined}>
      {tabPanels.map(({ key, props: column }) => {
        return (
          <PlTab key={key} fullWidth={fullWidth} variant={variant}>
            {column.label}
          </PlTab>
        );
      })}
    </PlTabList>
  );

  return (
    <TabGroup
      as={Fragment}
      defaultIndex={defaultIndex}
      selectedIndex={selectedIndex}
      onChange={onChange}
    >
      {suffix ? (
        <div className={clstx('flex items-center gap-x-1', className)}>
          {tabList}
          {suffix}
        </div>
      ) : (
        tabList
      )}
      <PlTabPanels className={classNameTabPanels}>
        {tabPanels.map(({ key, props: column }) => {
          return (
            <TabPanel key={key} className={column.className}>
              {({ selected }) => column.render(selected)}
            </TabPanel>
          );
        })}
      </PlTabPanels>
    </TabGroup>
  );
};

function useScrollOverflow<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    // Observe the container only — child resize shows up in the container's
    // `scrollWidth`, which `update` reads. Per-child observation goes stale as
    // soon as tabs are added/removed.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [update]);

  const scrollByAmount = useCallback((delta: number) => {
    ref.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  return { ref, canScrollLeft, canScrollRight, scrollByAmount };
}

const ScrollChevron: FC<{
  direction: 'left' | 'right';
  visible: boolean;
  onClick: () => void;
}> = ({ direction, visible, onClick }) => (
  <Button
    type="button"
    aria-label={direction === 'left' ? 'Scroll tabs left' : 'Scroll tabs right'}
    aria-hidden={!visible}
    tabIndex={visible ? 0 : -1}
    onClick={onClick}
    variant="quaternary"
    disabled={!visible}
  >
    <UntitledIcon
      name={direction === 'right' ? 'chevron-right' : 'chevron-left'}
      className="h-4 w-4"
    />
  </Button>
);

export const PlTabPanels: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => (
  <TabPanels className={clstx('mt-4 w-full', className)}>{children}</TabPanels>
);

export const PlTab: FC<
  PropsWithChildren<{ fullWidth: boolean; variant: Variant }>
> = ({ fullWidth, variant = 'default', children }) => (
  <Tab
    className={clsx(
      // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Line height between leading-none and leading-tight
      'leading-[1.15] tab',
      variant === 'default' && 'tab--underline',
      variant === 'segment' && 'tab--segment',
      variant === 'pilled' && 'tab--pilled',
      fullWidth && 'flex-1'
    )}
  >
    {children}
  </Tab>
);

export const PlTabList: FC<
  PropsWithChildren<{ variant: Variant; className?: string }>
> = ({ variant = 'default', className, children }) => (
  <TabList
    className={clsx(
      'typography:prose',
      className,
      (variant === 'default' || variant === 'pilled') && 'tabs',
      variant === 'segment' && 'tabs--segment'
    )}
  >
    {children}
  </TabList>
);

export default Object.assign(Tabs, {
  Panel,
});
